import axios, { AxiosResponse } from 'axios';
import { AppVersion } from '../lib/utils';
import WithBootedClient from '../lib/WithBootedClient';
import { ICountItem } from '../models/ICountItem';
import IJwt from '../models/IJwt';
import { ITask } from '../models/tasks/ITask';
import { ITaskFilter } from '../models/tasks/ITaskFilter';
import { IUserTasksByCategory } from '../models/tasks/IUserTasksByCategory';
import { ITaskMetadata } from '../models/tasks/metadatas/ITaskMetadata';
import { IUser } from '../models/users/IUser';
import RESTClient, { IArrayRestResponse } from './RESTClient';
import UserClient from './UserClient';
import Secrets from '@yumi/secrets';

interface IConfig {
  baseURL: string;
}

interface UpdateData {
  task_id: string;
  meta_data: unknown;
}

const formatTaskQuery = (filter?: ITaskFilter) => {
  let query = '';
  if (filter) {
    const keys = Object.keys(filter);
    query = keys.reduce((acc, current, idx) => {
      if (idx === 0) return `${acc}${current}=${(filter as any)[current]}`;
      return `${acc}&${current}=${(filter as any)[current]}`;
    }, '?');
  }
  return query;
};

class TaskClient extends RESTClient implements WithBootedClient {
  constructor(config: IConfig) {
    super({ baseURL: config.baseURL });
  }

  async boot() { }

  async getByCategory<T extends ITaskMetadata>(filter?: ITaskFilter): Promise<IUserTasksByCategory<T>> {
    const data = await UserClient.getTasks(filter);
    return {
      category: {
        id: filter?.category,
        name: filter?.category?.toLocaleLowerCase(),
      },
      items: data,
    } as IUserTasksByCategory<T>;
  }

  async updateTask(task_id: string, meta_data: ITaskMetadata, taskType: string) {
    await this.axios.put(`/tasks/${taskType}/${task_id}`, meta_data);
  }

  async updateTaskSync(task_id: string, meta_data: ITaskMetadata, taskType: string) {
    await this.axios.put(`/tasks/${taskType}/${task_id}?sync="true"`, meta_data);
  }

  async updateTaskV2(taskType: string, updates: UpdateData[]) {
    await this.axios.post(`${Secrets.REACT_APP_API_INJECTOR_URL}/tasksUpdated/CL/${taskType}`, {
      updates: updates.map((data) => ({
        type: taskType,
        data,
      })),
    });
  }

  async updateTaskIngressmer(taskType: string, updates: UpdateData[]) {
    await this.axios.post(`${Secrets.REACT_APP_INGRESSMER_API}/tasksUpdated/CL/${taskType}`, {
      updates: updates.map((data) => ({
        type: taskType,
        data,
      })),
    });
  }

  async getById<T extends ITaskMetadata>(taskId: string): Promise<ITask<T>> {
    const response = await this.axios.get(`/tasks/${taskId}`);
    return response.data as ITask<T>;
  }

  async markAsCompletedWH(taskId: string, data: any): Promise<void> {
    await axios.post(`https://webhook.site/c8916ae5-4c46-49f5-a517-e4e8649782b1/`, data);
  }

  async markAsCompleted(taskId: string): Promise<void> {
    return await this.axios.put(`/users/me/tasks/${taskId}`);
  }

  async completeTask(id: string): Promise<AxiosResponse> {
    return this.axios.put(`/users/me/tasks/${id}`);
  }

  async markAsState(taskId: string, state: 'DOING' | 'PENDING' | 'COMPLETED' | 'CANCELED' | 'PENDING_TO_REVIEW'): Promise<void> {
    await this.axios.put(`/users/me/tasks/${taskId}/state/${state}`);
  }

  async reportMultiTask(taskType: string, data: any): Promise<void> {
    return await this.axios.post(`/users/me/multiTask/report/${taskType}`, data);
  }

  async reportTask(taskType: string, jwt: IJwt, data: any): Promise<void> {
    const response = await axios.post(`${Secrets.REACT_APP_API_INJECTOR_URL}/tasksReport/CL/${taskType}`, data, {
      headers: {
        Authorization: `${jwt.token_type.toLowerCase()} ${jwt.access_token}`,
        'App-Version': AppVersion,

        ...(Secrets.REACT_APP_ENVIRONMENT === 'STAGING'
          ? {
            'CF-Access-Client-Id': Secrets.CF_ACCESS_CLIENT_ID,
            'CF-Access-Client-Secret': Secrets.CF_ACCESS_CLIENT_SECRET,
          }
          : {}),
      },
    });
    return response.data;
  }

  async getPartnerUsers(taskType: string): Promise<Array<IUser>> {
    const response = await this.axios.get(`tasks/partners/${taskType}`);
    return response.data as Array<IUser>;
  }

  async getByType(filter: ITaskFilter): Promise<IArrayRestResponse<ITask<any>>> {
    const query = formatTaskQuery(filter);
    const response = await this.axios.get(`/tasks/taskType/${filter.taskType}${query}`);
    return response.data as IArrayRestResponse<ITask<any>>;
  }

  async getByUserCreated(filter: ITaskFilter): Promise<Array<ICountItem>> {
    const query = formatTaskQuery(filter);
    const response = await this.axios.get(`/users/me/tasks/created-by/${query}`);
    return response.data as Array<ICountItem>;
  }

  async createTask(data: any, jwt: IJwt, taskType: string): Promise<void> {
    const response = await axios.post(`${Secrets.REACT_APP_API_INJECTOR_URL}/tasks/CL/${taskType}`, data, {
      headers: {
        Authorization: `${jwt.token_type.toLowerCase()} ${jwt.access_token}`,
        'App-Version': AppVersion,

        ...(Secrets.REACT_APP_ENVIRONMENT === 'STAGING'
          ? {
            'CF-Access-Client-Id': Secrets.CF_ACCESS_CLIENT_ID,
            'CF-Access-Client-Secret': Secrets.CF_ACCESS_CLIENT_SECRET,
          }
          : {}),
      },
    });
    return response.data;
  }

  async createTaskIngressmer(data: any, jwt: IJwt, taskType: string): Promise<void> {
    const response = await axios.post(`${Secrets.REACT_APP_INGRESSMER_API}/tasks/CL/${taskType}`, data, {
      headers: {
        Authorization: `${jwt.token_type.toLowerCase()} ${jwt.access_token}`,

        'App-Version': AppVersion,

        ...(Secrets.REACT_APP_ENVIRONMENT === 'STAGING'
          ? {
            'CF-Access-Client-Id': Secrets.CF_ACCESS_CLIENT_ID,
            'CF-Access-Client-Secret': Secrets.CF_ACCESS_CLIENT_SECRET,
          }
          : {}),
      },
    });
    return response.data;
  }

  async createReport(data: any, taskType: string, operation?: string, withResponse?: boolean): Promise<any> {
    data.operation = operation ? operation : 'add';
    const response = await this.axios.post(`/tasks/report/${taskType}`, data);
    return withResponse ? response.data.response : response.data.ok;
  }

  async createReportSync(data: any, taskType: string, operation: string = 'add', completeTask = false): Promise<any> {
    data.operation = operation;
    const response = await this.axios.post(`/tasks/report/sync/${taskType}?completeTask=${completeTask}`, data);
    return response.data;
  }

  async uploadPhoto(taskId: string, base64Image: string, customName: string = ''): Promise<any> {
    const result = await this.axios.post(`/tasks/images`, {
      created_at: new Date(),
      image: base64Image,
      task: taskId,
      custom_name: customName,
    });
    return result.data;
  }

  public getByIdAxios(taskId: string) {
    return this.axios.get(`/tasks/${taskId}`);
  }

  async uploadImage(image: string, name: string) {
    return await this.axios.post(`/tasks/images/upload`, { image, name });
  }

  async assignMeTask(task_id: string, taskType: string) {
    const user = await UserClient.me();
    const meta_data = { assinged_to: [user.primarysid] };
    const updates = [{ task_id, meta_data }]; // always we only send one update request with user to assign finally
    await this.axios.post(`${Secrets.REACT_APP_API_INJECTOR_URL}/tasksUpdated/CL/${taskType}`, {
      updates: updates.map((data) => ({
        type: taskType,
        data,
      })),

    });
  }
}

export default new TaskClient({
  baseURL: Secrets.REACT_APP_API_ENDPOINT!,
});
